import React, { useState, useMemo } from 'react';
import { BookOpen, Search, Filter, Mail, Phone, Calendar, Building2, Users, ShieldCheck, ShieldOff, Clock, Info, X, Key, ChevronUp, ChevronDown, User, Ban, ShieldAlert } from '../DeskeyIcons';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { ReasonConfirmModal } from '../ui/ReasonConfirmModal';
import { toast } from 'sonner';
import { notifyError } from '../../lib/errors';
import { institutionApi } from '../../lib/institutionApi';

const api = institutionApi;

const StatusBadge = ({ status }) => {
  const styles = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    none: 'bg-gray-50 text-gray-400 border-gray-200',
  };
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${styles[status] || styles.none}`}>
      {status === 'active' ? 'Active' : status === 'pending' ? 'Pending' : 'No Licence'}
    </span>
  );
};

const ProfileModal = ({ person, accent, onClose, onBlock, onUnblock, readOnly }) => {
  if (!person) return null;
  const A = accent;
  const initial = (person.full_name || '?')[0].toUpperCase();
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' }) : '—';
  const isBlocked = !!person.blocked;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="profile-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header band */}
        <div className="h-28 relative" style={{ background: `linear-gradient(135deg, ${A}, ${A}cc)` }}>
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors" data-testid="close-profile-modal">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Avatar */}
        <div className="flex justify-center -mt-14">
          {person.profile_photo ? (
            <img src={person.profile_photo} alt="" className="w-28 h-28 rounded-full border-4 border-white shadow-lg object-cover" />
          ) : (
            <div className="w-28 h-28 rounded-full border-4 border-white shadow-lg flex items-center justify-center text-white text-3xl font-semibold" style={{ background: A }}>
              {initial}
            </div>
          )}
        </div>

        {/* Name + Role */}
        <div className="text-center mt-3 px-6">
          <h3 className="text-xl font-semibold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{person.full_name}</h3>
          <p className="text-xs text-gray-400 mt-0.5 font-medium">Teacher</p>
          <div className="flex items-center justify-center gap-2 mt-1">
            <StatusBadge status={person.licence_status || 'none'} />
            {isBlocked && <span className="text-xs px-2.5 py-1 rounded-full font-semibold border bg-red-50 text-red-600 border-red-200">Blocked</span>}
          </div>
        </div>

        {/* Bio */}
        <div className="px-6 mt-4">
          <p className="text-sm text-gray-500 text-center leading-relaxed">
            {person.bio || 'No bio added yet.'}
          </p>
        </div>

        {/* Info grid */}
        <div className="px-6 mt-5 space-y-0 divide-y divide-gray-100">
          <InfoRow icon={Mail} label="Email" value={person.email} accent={A} />
          <InfoRow icon={Phone} label="Phone" value={person.phone || 'Not provided'} accent={A} />
          <InfoRow icon={Building2} label="Departments" value={person.department_names || 'Unassigned'} accent={A} />
          <InfoRow icon={Users} label="Classes" value={`${person.classes_count || 0} class${(person.classes_count || 0) !== 1 ? 'es' : ''}`} accent={A} />
          <InfoRow icon={Calendar} label="Joined" value={formatDate(person.created_at)} accent={A} />
          {person.licence_key && <InfoRow icon={Key} label="Licence key" value={person.licence_key} accent={A} mono />}
        </div>

        {/* Actions */}
        {!readOnly && (
          <div className="px-6 py-4 border-t border-gray-100 mt-2">
            {isBlocked ? (
              <button
                onClick={() => onUnblock && onUnblock(person.id)}
                className="w-full h-9 rounded-lg text-sm font-semibold text-emerald-600 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors flex items-center justify-center gap-1.5"
                data-testid={`unblock-teacher-${person.id}`}
              >
                <ShieldCheck className="w-4 h-4" /> Unblock teacher
              </button>
            ) : (
              <button
                onClick={() => onBlock && onBlock(person)}
                className="w-full h-9 rounded-lg text-sm font-semibold text-red-500 border border-red-200 bg-red-50 hover:bg-red-100 transition-colors flex items-center justify-center gap-1.5"
                data-testid={`block-teacher-${person.id}`}
              >
                <Ban className="w-4 h-4" /> Block teacher
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value, accent, mono }) => (
  <div className="flex items-center gap-3 py-3">
    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${accent}10` }}>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-xs text-gray-400 font-medium">{label}</p>
      <p className={`text-sm text-gray-800 truncate ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
    </div>
  </div>
);

export function TeachersSection({ teachers, accent, onRevoke, readOnly }) {
  const A = accent || '#00AA8D';
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [blockTarget, setBlockTarget] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const allDepartments = useMemo(() => {
    const deptMap = {};
    teachers.forEach(t => (t.departments || []).forEach(d => { deptMap[d.id] = d.name; }));
    return Object.entries(deptMap).map(([id, name]) => ({ id, name }));
  }, [teachers]);

  const filtered = useMemo(() => {
    let list = [...teachers];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(t => t.full_name?.toLowerCase().includes(q) || t.email?.toLowerCase().includes(q) || t.phone?.includes(q));
    }
    if (statusFilter !== 'all') list = list.filter(t => (t.licence_status || 'none') === statusFilter);
    if (deptFilter !== 'all') list = list.filter(t => (t.departments || []).some(d => d.id === deptFilter));
    list.sort((a, b) => {
      let va, vb;
      if (sortBy === 'name') { va = a.full_name || ''; vb = b.full_name || ''; }
      else if (sortBy === 'classes') { va = a.classes_count || 0; vb = b.classes_count || 0; }
      else if (sortBy === 'date') { va = a.created_at || ''; vb = b.created_at || ''; }
      else { va = a.licence_status || ''; vb = b.licence_status || ''; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [teachers, search, statusFilter, deptFilter, sortBy, sortDir]);

  const stats = useMemo(() => ({
    total: teachers.length,
    active: teachers.filter(t => t.licence_status === 'active').length,
    pending: teachers.filter(t => t.licence_status === 'pending').length,
    unassigned: teachers.filter(t => !t.departments || t.departments.length === 0).length,
  }), [teachers]);

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };
  const SortIcon = ({ field }) => sortBy === field ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : null;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div className="flex flex-col gap-4 h-[calc(100vh-120px)]" data-testid="teachers-section">
      {selectedPerson && (
        <ProfileModal
          person={selectedPerson}
          accent={A}
          onClose={() => setSelectedPerson(null)}
          onBlock={(p) => { setSelectedPerson(null); setBlockTarget(p); }}
          onUnblock={async (id) => {
            try {
              const res = await api(`/institution/teachers/${id}/unblock`, { method: 'POST' });
              toast.success(res.message || 'Teacher unblocked'); setSelectedPerson(null);
            } catch (e) { notifyError(e, 'Could not unblock. Please try again.'); }
          }}
          readOnly={readOnly}
        />
      )}

      {blockTarget && (
        <ReasonConfirmModal
          open={!!blockTarget}
          onClose={() => setBlockTarget(null)}
          title={`Block ${blockTarget.full_name}`}
          description="Enter a reason for blocking this teacher."
          accent="#ef4444"
          confirmLabel="Confirm Block"
          onConfirm={async (reason) => {
            try {
              const res = await api(`/institution/teachers/${blockTarget.id}/block`, {
                method: 'POST', body: JSON.stringify({ reason })
              });
              toast.success(res.message || 'Teacher blocked'); setBlockTarget(null);
            } catch (e) { notifyError(e, 'Could not block. Please try again.'); throw e; }
          }}
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 flex-shrink-0">
        {[
          { label: 'Total teachers', val: stats.total, icon: BookOpen, color: A },
          { label: 'Active', val: stats.active, icon: ShieldCheck, color: '#1A7F37' },
          { label: 'Pending', val: stats.pending, icon: Clock, color: '#f59e0b' },
          { label: 'Unassigned', val: stats.unassigned, icon: ShieldOff, color: '#94a3b8' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-gray-200 rounded-xl shadow p-4 transition duration-300 hover:shadow-md group" data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center transition-transform" style={{ background: `${s.color}14` }}>
                <s.icon className="w-4 h-4" style={{ color: s.color }} strokeWidth={1.75} />
              </div>
              <span className="text-xs font-semibold text-gray-500">{s.label}</span>
            </div>
            <p className="text-2xl font-semibold text-gray-900 tabular-nums tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="bg-white border border-gray-200 rounded-xl shadow p-3 flex-shrink-0">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or phone..." className="pl-9 h-9 text-sm rounded-lg border-gray-200" data-testid="teacher-search" />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-gray-500" />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-9 text-xs rounded-lg border border-gray-200 px-3 bg-white text-gray-700 focus:outline-none" data-testid="teacher-status-filter">
              <option value="all">All status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="none">No licence</option>
            </select>
            {allDepartments.length > 0 && (
              <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} className="h-9 text-xs rounded-lg border border-gray-200 px-3 bg-white text-gray-700 focus:outline-none" data-testid="teacher-dept-filter">
                <option value="all">All departments</option>
                {allDepartments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
          </div>
          <span className="text-xs text-gray-500 ml-auto font-medium">{filtered.length} of {teachers.length}</span>
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl shadow text-center py-16 flex-1">
          <BookOpen className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-500 font-medium">{teachers.length === 0 ? 'No teachers yet' : 'No teachers match your filters'}</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl shadow overflow-hidden flex-1 min-h-0 flex flex-col">
          <div className="flex-1 overflow-y-auto min-h-0">
            <table className="w-full text-sm" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              <thead className="bg-gray-50/80 border-b border-gray-100 sticky top-0 z-10">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 cursor-pointer select-none" onClick={() => toggleSort('name')}>
                  <span className="flex items-center gap-1">Teacher <SortIcon field="name" /></span>
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">Department</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 cursor-pointer select-none" onClick={() => toggleSort('classes')}>
                  <span className="flex items-center gap-1">Classes <SortIcon field="classes" /></span>
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 cursor-pointer select-none" onClick={() => toggleSort('status')}>
                  <span className="flex items-center gap-1">Status <SortIcon field="status" /></span>
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 cursor-pointer select-none" onClick={() => toggleSort('date')}>
                  <span className="flex items-center gap-1">Joined <SortIcon field="date" /></span>
                </th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors" data-testid={`teacher-row-${t.id}`}>
                  <td className="px-5 py-3.5">
                    <button onClick={() => setSelectedPerson(t)} className="flex items-center gap-3 hover:opacity-80 transition-opacity text-left" data-testid={`teacher-click-${t.id}`}>
                      {t.profile_photo
                        ? <img src={t.profile_photo} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                        : <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0" style={{ background: A }}>{(t.full_name || '?')[0].toUpperCase()}</div>}
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{t.full_name}</p>
                        <p className="text-xs text-gray-400">{t.email}</p>
                      </div>
                    </button>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-1">
                      {(t.departments || []).length > 0
                        ? t.departments.map(d => <span key={d.id} className="text-xs px-2 py-0.5 rounded-md font-medium bg-blue-50 text-blue-600 border border-blue-100">{d.name}</span>)
                        : <span className="text-xs text-gray-400">Unassigned</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700">
                      <Users className="w-3 h-3 text-gray-400" /> {t.classes_count || 0}
                    </span>
                  </td>
                  <td className="px-5 py-3.5"><StatusBadge status={t.licence_status || 'none'} /></td>
                  <td className="px-5 py-3.5 text-xs text-gray-400">{formatDate(t.created_at)}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setSelectedPerson(t)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors group" data-testid={`info-${t.id}`} title="View profile">
                        <Info className="w-4 h-4 text-gray-400 group-hover:text-gray-700 transition-colors" />
                      </button>
                      {!readOnly && t.licence_status === 'active' && (
                        <button onClick={() => onRevoke(t.id)} className="text-xs text-red-500 hover:text-red-700 font-medium hover:underline transition-colors" data-testid={`revoke-${t.id}`}>
                          Revoke
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
